import { FC, PropsWithChildren, ReactElement } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Price } from '@eneco/flows/components';
import { Badge, Box, Divider, Heading, RadioCard, Stack, Stretch, Text } from '@eneco/sparky';
import { CheckIcon, CloseIcon } from '@eneco/sparky/icons';
import { Products_Offers_V3_OfferProductResponseModel } from '@monorepo-types/dc';
import type { ServiceGemakProductUspListField } from '@sitecore/types/ServiceGemakFlowOfferStep';
import { Fields } from '@sitecore/types/ServiceGemakFlowOfferStep';

import { selectCostDetailsByType } from '../../helpers/dc';

type Usp = {
  text?: string;
  icon?: ReactElement;
};
type Usps = Array<Usp>;

interface Props {
  productData: Products_Offers_V3_OfferProductResponseModel;
  textualData?: Fields;
  isSME?: boolean;
}

type BaseProductProps = Pick<Props, 'productData' | 'textualData'>;

const getProductTextualData = ({ textualData, productData }: Props) => {
  const mpc = productData?.marketingProductCode;
  if (mpc == null) {
    return null;
  }
  return textualData?.productSettings?.serviceGemakProductsList?.filter?.(
    i => i.fields?.productProperties?.marketingProductCode?.value === mpc,
  )?.[0];
};

const getUsps = (uspList?: ServiceGemakProductUspListField): Usps => {
  return (
    uspList?.map(usp => ({
      text: usp?.fields?.content?.text?.value,
      icon:
        usp?.fields?.content?.icon?.value === 'CheckIcon' ? (
          <CheckIcon color="feedbackSuccess" />
        ) : (
          <CloseIcon color="feedbackError" />
        ),
    })) ?? []
  );
};

const ProductTitle: FC<BaseProductProps> = ({ textualData, productData: { marketingProductCode } }) => {
  const item = textualData?.productSettings.serviceGemakProductsList.find(
    i => i.fields?.productProperties?.marketingProductCode?.value === marketingProductCode,
  );

  const title = item?.fields?.content?.productNameText?.value;
  return (
    title && (
      <Text id={`${marketingProductCode}-label`} weight="bold">
        {title}
      </Text>
    )
  );
};

const ProductPrice: FC<Props> = ({ textualData, productData, isSME }) => {
  const costsType = isSME ? 'vatExcluded' : 'vatIncluded';
  const monthlyPrice = selectCostDetailsByType('monthlyCosts', productData?.costDetails)?.[costsType] ?? 0;
  const period = textualData?.productContent.perMonthLabel?.value;

  return (
    monthlyPrice && (
      <Stack.Item shrink={{ initial: true, lg: false }}>
        <Text align="right">
          <Price id={`${productData?.marketingProductCode}-price`} suffix={period}>
            {monthlyPrice}
          </Price>
        </Text>
      </Stack.Item>
    )
  );
};

const UspList: FC<{ usps?: Usps; title?: string }> = ({ usps, title }) => {
  return (
    <Stack direction="column" gap={1}>
      <Stack.Item>
        {title && (
          <Heading size="3XS" as="h2">
            {title}
          </Heading>
        )}
      </Stack.Item>
      <Stack.Item>
        <Stack direction="column" gap="1">
          {usps?.map(usp => (
            <Stack direction="row" key={usp.text} gap={1}>
              <Stack.Item>{usp.icon}</Stack.Item>
              <Stack.Item>
                <RichText html={usp.text} />
              </Stack.Item>
            </Stack>
          ))}
        </Stack>
      </Stack.Item>
    </Stack>
  );
};

const ProductBadge: FC<BaseProductProps> = ({ textualData, productData }) => {
  const item = textualData?.productSettings.serviceGemakProductsList.find(
    i => i.fields?.productProperties?.marketingProductCode?.value === productData.marketingProductCode,
  );

  return (
    item && (
      <Box paddingBottom="4">
        <Badge color="success">{item.fields?.content?.badgeText?.value ?? ''}</Badge>
      </Box>
    )
  );
};

export const ProductCard: FC<PropsWithChildren<BaseProductProps>> = ({ productData, textualData, children }) => {
  const id = productData?.marketingProductCode ?? '';
  const productTextualData = getProductTextualData({ productData, textualData });

  return (
    <Stretch>
      <RadioCard value={id} ariaLabelledby={`${id}-label ${id}-price`}>
        <Stretch>
          <Stack direction="column">
            <Stack.Item grow={true}>
              <Box padding="6">
                <Stack gap="2">
                  <Stack direction="row" alignX="justify" gap="2">
                    <Stack direction="row" gap="2" alignY="center">
                      <Stack.Item shrink={false}>
                        <RadioCard.Indicator />
                      </Stack.Item>
                      <ProductTitle productData={productData} textualData={textualData} />
                    </Stack>

                    <ProductPrice productData={productData} textualData={textualData} />
                  </Stack>
                  <Stack.Item>
                    <ProductBadge textualData={textualData} productData={productData} />
                  </Stack.Item>
                  <Stack gap={4}>
                    <Stack.Item>
                      <UspList
                        title={productTextualData?.fields?.usp?.maintenanceUspTitle?.value}
                        usps={getUsps(productTextualData?.fields?.usp?.maintenanceUspList)}
                      />
                    </Stack.Item>
                    <Stack.Item>
                      <Divider />
                    </Stack.Item>
                    <Stack.Item>
                      <UspList
                        title={productTextualData?.fields?.usp?.malfunctionUspTitle?.value}
                        usps={getUsps(productTextualData?.fields?.usp?.malfunctionUspList)}
                      />
                    </Stack.Item>
                  </Stack>
                </Stack>
              </Box>
            </Stack.Item>

            {children && (
              <Stack.Item>
                <Divider />
                {children}
              </Stack.Item>
            )}
          </Stack>
        </Stretch>
      </RadioCard>
    </Stretch>
  );
};
